"use client";

import { Users, CalendarHeart } from 'lucide-react';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { JartinetHermandad } from '@/lib/types';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { Skeleton } from '../ui/skeleton';

const features = [
  {
    icon: <CalendarHeart className="h-12 w-12 text-primary" />,
    title: "Eventos",
    description: "Además de los eventos cofrades, contamos con todo tipo de eventos como feria, Rocío, verbenas, programas y concursos de televisión, conciertos, etc."
  },
  {
    icon: <Users className="h-12 w-12 text-primary" />,
    title: "Comunidad",
    description: "Aquí encontrarás a mucha gente que compartirá tus mismos gustos y podrás forjar grupos y amistades entrañables en tu experiencia en Jartiland."
  }
];

export function About() {
  const [hermandades, setHermandades] = useState<JartinetHermandad[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchHermandades() {
      try {
        const querySnapshot = await getDocs(collection(db, 'jartinet_hermandades'));
        const hermandadesData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as JartinetHermandad[];
        const hermandadesConEscudo = hermandadesData.filter(h => h.escudoUrl);
        setHermandades(hermandadesConEscudo);
      } catch (error) {
        console.error("Failed to fetch hermandades for about section:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchHermandades();
  }, []);

  const shieldsCarousel = () => {
    if (loading) {
      return (
        <div className="relative h-16 w-full flex items-center justify-center overflow-hidden">
          <div className="flex gap-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-14 w-14 rounded-full" />
            ))}
          </div>
        </div>
      );
    }

    if (hermandades.length === 0) return null;

    return (
       <div className="relative h-16 w-full flex items-center justify-center overflow-hidden">
        <motion.div
          className="flex"
          animate={{ x: ['0%', '-100%'] }}
          transition={{
            ease: 'linear',
            duration: hermandades.length * 4, // Adjust duration based on number of shields
            repeat: Infinity,
          }}
        >
          {/* Duplicamos el array para un bucle infinito y fluido */}
          {[...hermandades, ...hermandades].map((escudo, index) => (
            <div key={index} className="flex-shrink-0 w-24 h-16 flex items-center justify-center">
              <img
                src={escudo.escudoUrl}
                alt={escudo.nombre}
                width={56}
                height={56}
                className="object-contain drop-shadow-lg h-14 w-auto"
              />
            </div>
          ))}
        </motion.div>
      </div>
    );
  };


  return (
    <section className="relative w-full overflow-hidden bg-background/85 py-24 md:py-32">
      <div className="absolute -top-24 right-0 h-96 w-96 rounded-full bg-primary/10 blur-[120px]" />
      <div className="absolute bottom-0 left-0 h-72 w-72 rounded-full bg-primary/5 blur-[100px]" />

      <div className="container relative mx-auto px-4">
        <div className="mx-auto max-w-3xl text-center">
          <motion.span
            className="inline-block text-xs font-bold uppercase tracking-[0.35em] text-primary"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.5 }}
          >
            Conócenos
          </motion.span>
          <motion.h2
            className="mt-3 font-headline text-4xl font-bold md:text-5xl"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            ¿Qué es{" "}
            <span className="bg-gradient-to-r from-primary to-amber-500 bg-clip-text text-transparent">
              Jartiland
            </span>
            ?
          </motion.h2>
          <motion.p
            className="mt-4 text-lg text-muted-foreground"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Jartiland es un servidor de Minecraft de la comunidad de DarioelJartible que está enfocado al ámbito cofrade. Contamos con un mapa digno de admirar con numerosas iglesias y lugares de Andalucía, así como con nuestro Mod Cofrade.
          </motion.p>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-6 text-center md:mt-20 md:grid-cols-3">
          <motion.div
            className="group relative flex flex-col items-center overflow-hidden rounded-2xl border border-white/8 bg-card/40 p-8 backdrop-blur-sm transition-colors hover:border-primary/30"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            whileHover={{ y: -6 }}
          >
            <div className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-primary to-amber-500 opacity-0 transition-opacity group-hover:opacity-100" />
            <div className="mb-4 flex h-16 w-full items-center justify-center">{shieldsCarousel()}</div>
            <h3 className="font-headline text-2xl font-semibold">Hermandades</h3>
            <p className="mt-4 text-muted-foreground">
              Podrás fundar hermandades desde cero, empezando con un grupo parroquial sin nada e ir enriqueciendo tu patrimonio hasta llegar a ser una hermandad de pleno derecho.
            </p>
          </motion.div>
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              className="group relative flex flex-col items-center overflow-hidden rounded-2xl border border-white/8 bg-card/40 p-8 backdrop-blur-sm transition-colors hover:border-primary/30"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
              whileHover={{ y: -6 }}
            >
              <div className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-primary to-amber-500 opacity-0 transition-opacity group-hover:opacity-100" />
              <div className="mb-4 flex h-16 w-full items-center justify-center">{feature.icon}</div>
              <h3 className="font-headline text-2xl font-semibold">{feature.title}</h3>
              <p className="mt-4 text-muted-foreground">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
